import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import StatisticsCards from './components/StatisticsCards';
import PropertyTable from './components/PropertyTable';
import FilterToolbar from './components/FilterToolbar';
import BulkActions from './components/BulkActions';
import QuickActions from './components/QuickActions';
import VideoManagementModal from './components/VideoManagementModal';
import NotaryAppointmentModal from './components/NotaryAppointmentModal';
import UserRequestModal from './components/UserRequestModal';
import PropertyFormModal from './components/PropertyFormModal';
import ObjAssetManager from './components/ObjAssetManager';

const PropertyManagementDashboard = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [filters, setFilters] = useState({
    commune: '',
    propertyType: '',
    status: '',
    agent: '',
    priceRange: { min: '', max: '' }
  });
  const [statistics, setStatistics] = useState({
    totalListings: 0,
    pendingApprovals: 0,
    activeProperties: 0,
    totalRevenue: 0
  });
  
  // Modal states
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [notaryModalOpen, setNotaryModalOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [propertyFormModalOpen, setPropertyFormModalOpen] = useState(false);
  const [objAssetManagerOpen, setObjAssetManagerOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);

  // Fetch properties with filters
  const fetchProperties = async () => {
    setLoading(true);
    try {
      // Enhanced query to fetch ALL current properties with complete details
      let query = supabase?.from('properties')?.select(`
          *,
          commune:communes(name, district, description, average_price),
          agent:user_profiles!agent_id(full_name, email, phone),
          owner:user_profiles!owner_id(full_name, email, phone)
        `);

      // Apply filters only if they have values - otherwise fetch ALL properties
      if (filters?.commune && filters?.commune !== '') {
        query = query?.eq('commune.name', filters?.commune);
      }
      if (filters?.propertyType && filters?.propertyType !== '') {
        query = query?.eq('property_type', filters?.propertyType);
      }
      if (filters?.status && filters?.status !== '') {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.agent && filters?.agent !== '') {
        query = query?.eq('agent_id', filters?.agent);
      }
      if (filters?.priceRange?.min && filters?.priceRange?.min !== '') {
        query = query?.gte('price', filters?.priceRange?.min);
      }
      if (filters?.priceRange?.max && filters?.priceRange?.max !== '') {
        query = query?.lte('price', filters?.priceRange?.max);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors de la récupération des propriétés:', error);
        throw error;
      }
      
      console.log(`✅ ${data?.length || 0} propriétés récupérées du système`, data);
      setProperties(data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      // Show error message to user
      alert('Erreur lors du chargement des propriétés. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced statistics fetch for all properties
  const fetchStatistics = async () => {
    try {
      // Get total count of ALL properties
      const { count: totalCount, error: totalError } = await supabase?.from('properties')?.select('*', { count: 'exact', head: true });

      // Get count by status
      const { count: pendingCount } = await supabase?.from('properties')?.select('*', { count: 'exact', head: true })?.eq('status', 'reserved');
      const { count: activeCount } = await supabase?.from('properties')?.select('*', { count: 'exact', head: true })?.eq('status', 'available');
      const { count: soldCount } = await supabase?.from('properties')?.select('*', { count: 'exact', head: true })?.eq('status', 'sold');

      // Calculate total revenue from sold properties
      const { data: revenueData } = await supabase?.from('properties')?.select('price')?.eq('status', 'sold');
      const totalRevenue = revenueData?.reduce((sum, prop) => sum + (Number(prop?.price) || 0), 0) || 0;

      const stats = {
        totalListings: totalCount || 0,
        pendingApprovals: pendingCount || 0,
        activeProperties: activeCount || 0,
        totalRevenue,
        soldProperties: soldCount || 0
      };

      console.log('📊 Statistiques des propriétés:', stats);
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  useEffect(() => {
    // Load all properties on component mount
    console.log('🏠 Chargement de toutes les propriétés du système...');
    fetchProperties();
    fetchStatistics();
  }, [filters]);

  // Handle bulk actions
  const handleBulkAction = async (action, params = {}) => {
    if (selectedProperties?.length === 0) return;

    try {
      let query = supabase?.from('properties');
      
      switch (action) {
        case 'status':
          query = query?.update({ status: params?.status })?.in('id', selectedProperties);
          break;
        case 'commune':
          query = query?.update({ commune_id: params?.communeId })?.in('id', selectedProperties);
          break;
        case 'price':
          query = query?.update({ price: params?.price })?.in('id', selectedProperties);
          break;
        default:
          return;
      }

      const { error } = await query;
      if (error) throw error;

      setSelectedProperties([]);
      fetchProperties();
      fetchStatistics();
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  // Handle individual property actions
  const handlePropertyAction = async (propertyId, action, params = {}) => {
    try {
      switch (action) {
        case 'edit':
          const propertyToEdit = properties?.find(p => p?.id === propertyId);
          setEditingProperty(propertyToEdit);
          setPropertyFormModalOpen(true);
          break;
        case 'videos':
          const propertyForVideo = properties?.find(p => p?.id === propertyId);
          setSelectedProperty(propertyForVideo);
          setVideoModalOpen(true);
          break;
        case 'obj-import':
          const propertyForObj = properties?.find(p => p?.id === propertyId);
          setSelectedProperty(propertyForObj);
          setObjAssetManagerOpen(true);
          break;
        case 'delete':
          if (window.confirm('Êtes-vous sûr de vouloir supprimer cette propriété ?')) {
            await supabase?.from('properties')?.delete()?.eq('id', propertyId);
            fetchProperties();
            fetchStatistics();
          }
          break;
        case 'approve': await supabase?.from('properties')?.update({ status: 'available' })?.eq('id', propertyId);
          break;
        case 'reject': await supabase?.from('properties')?.update({ status: 'withdrawn' })?.eq('id', propertyId);
          break;
        case 'feature': await supabase?.from('properties')?.update({ 
              features: { 
                ...properties?.find(p => p?.id === propertyId)?.features,
                featured: true 
              }
            })?.eq('id', propertyId);
          break;
        default:
          return;
      }

      fetchProperties();
      fetchStatistics();
    } catch (error) {
      console.error('Error performing property action:', error);
    }
  };

  // Handle quick actions
  const handleQuickAction = (action) => {
    switch (action) {
      case 'new-property':
        setEditingProperty(null);
        setPropertyFormModalOpen(true);
        break;
      case 'video-management': // ✅ Fix: Don't open video management without a property
        if (properties?.length === 0) {
          alert('Aucune propriété disponible. Veuillez d\'abord créer une propriété.');
          return;
        }
        alert('Veuillez sélectionner une propriété depuis le tableau pour gérer ses vidéos.');
        break;
      case 'obj-import':
        if (properties?.length === 0) {
          alert('Aucune propriété disponible. Veuillez d\'abord créer une propriété.');
          return;
        }
        setSelectedProperty(properties?.[0]);
        setObjAssetManagerOpen(true);
        break;
      case 'notary-appointments':
        setNotaryModalOpen(true);
        break;
      case 'user-requests':
        setRequestModalOpen(true);
        break;
      case 'document-verification':
        console.log('Document verification action');
        break;
      case 'agent-management': console.log('Agent management action');
        break;
      case 'approval-workflow': console.log('Approval workflow action');
        break;
      case 'notification-center':
        console.log('Notification center action');
        break;
      case 'market-analysis': console.log('Market analysis action');
        break;
      case 'performance-tracking':
        console.log('Performance tracking action');
        break;
      case 'bulk-operations': console.log('Bulk operations action');
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const refreshData = () => {
    console.log('🔄 Actualisation des données des propriétés...');
    fetchProperties();
    fetchStatistics();
  };

  return (
    <>
      <Helmet>
        <title>Gestion des Propriétés - Toutes les Propriétés Actuelles</title>
        <meta name="description" content="Interface administrative complète pour la gestion de toutes les propriétés actuelles du système immobilier" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Enhanced Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Tableau de Bord - Gestion de Toutes les Propriétés
            </h1>
            <p className="text-gray-600">
              Interface administrative complète pour la supervision et la gestion de toutes les propriétés actuelles du système, 
              incluant les vidéos, rendez-vous et demandes clients
            </p>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>📋 Propriétés Système:</strong> Cette interface affiche toutes les propriétés actuellement enregistrées dans le système, 
                incluant tous leurs détails, médias et informations de gestion.
              </p>
            </div>
          </div>

          {/* Statistics Cards */}
          <StatisticsCards statistics={statistics} />

          {/* Filter Toolbar */}
          <FilterToolbar 
            filters={filters}
            setFilters={setFilters}
            onRefresh={refreshData}
          />

          {/* Bulk Actions */}
          {selectedProperties?.length > 0 && (
            <BulkActions
              selectedCount={selectedProperties?.length}
              onBulkAction={handleBulkAction}
            />
          )}

          {/* Quick Actions */}
          <QuickActions 
            onRefresh={refreshData}
            onAction={handleQuickAction}
          />

          {/* Enhanced Property Table */}
          <PropertyTable
            properties={properties}
            loading={loading}
            selectedProperties={selectedProperties}
            setSelectedProperties={setSelectedProperties}
            onPropertyAction={handlePropertyAction}
          />
        </div>
      </div>

      {/* Modals */}
      <ObjAssetManager
        property={selectedProperty}
        isOpen={objAssetManagerOpen}
        onClose={() => {
          setObjAssetManagerOpen(false);
          setSelectedProperty(null);
        }}
        onAssetsImported={refreshData}
      />

      <VideoManagementModal
        property={selectedProperty}
        isOpen={videoModalOpen}
        onClose={() => {
          setVideoModalOpen(false);
          setSelectedProperty(null);
        }}
        onUpdate={refreshData}
      />

      <NotaryAppointmentModal
        isOpen={notaryModalOpen}
        onClose={() => setNotaryModalOpen(false)}
        onUpdate={refreshData}
      />

      <UserRequestModal
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        onUpdate={refreshData}
      />

      <PropertyFormModal
        property={editingProperty}
        isOpen={propertyFormModalOpen}
        onClose={() => {
          setPropertyFormModalOpen(false);
          setEditingProperty(null);
        }}
        onSave={refreshData}
      />
    </>
  );
};

export default PropertyManagementDashboard;